/**
 * @import {GetServerSideProps} from 'next'
 * @import {GetServerSideAuthProps} from './utils'
 * @import {ReactNode} from 'react'
 */

import { Box } from '@radix-ui/themes'
import { getSession } from 'next-auth/react'
import * as Dialog from '../../components/dialog'
import { Logo } from '../../components/logo'
import { ErrorPage } from '../_error'
import * as ResetPage from './reset'
import * as SignInPage from './signin'
import * as SignUpPage from './signup'
import * as SSOCallBackPage from './sso-callback'
import * as SSOAuthorizePage from './sso-authorize'
import Image from 'next/image'
import { useEnv } from '../../context/env'
import { notFound, Actions } from './utils'

import styles from './auth.module.css'
import { PasswordValidation } from './validation'
import { pack } from '@synqly/fun'

export { SynqlyAuth, PasswordValidation }

/**
 * @param {{
 *   slugParam: string
 *   canSignIn: boolean
 *   canSignUp: boolean
 *   canReset: boolean
 *   callbackUrl?: string
 * }} config
 */
function SynqlyAuth({
  slugParam,
  canSignIn,
  canSignUp,
  canReset,
  callbackUrl: defaultCallbackUrl = '/',
}) {
  AuthPage.Layout = AuthModal

  const ActionPages = {
    [Actions.SignIn]: canSignIn && SignInPage,
    [Actions.SignUp]: canSignUp && SignUpPage,
    [Actions.Reset]: canReset && ResetPage,
    [Actions.Callback]: canSignIn && SSOCallBackPage,
    [Actions.SingleSignOn]: canSignIn && SSOAuthorizePage,
  }

  return { AuthPage, getServerSideProps }

  /** @type {GetServerSideProps} */
  async function getServerSideProps(context) {
    // If we have a session we don't have to do anything else
    const session = await getSession({ broadcast: false })
    if (session) {
      return {
        redirect: {
          destination: String(context.query.callbackUrl) || '/',
          permanent: false,
        },
      }
    }

    const {
      [slugParam]: actionSegments,
      callbackUrl,
      error: errorMessage,
      ...query
    } = context.query
    const [action, ...params] = [actionSegments].flat()

    if (!ActionPages[action]) {
      return notFound(context)
    }

    const { NEXT_PUBLIC_APP_URL } = process.env
    const url = new URL(context.resolvedUrl, NEXT_PUBLIC_APP_URL)
    const mountPath = url.pathname
      .split('/')
      .slice(0, -actionSegments.length)
      .join('/')

    const routes = Object.fromEntries(
      Object.entries(ActionPages).map(([action, ActionPage]) => {
        const routeParams = ActionPage['routeParams'] ?? ''

        return [
          action,
          ActionPage ? `${mountPath}/${action}/${routeParams}` : false,
        ]
      }),
    )

    const authContext = {
      action,
      params,
      routes,
    }

    const { getServerSideProps = defaultPageProps } = ActionPages[action]
    try {
      const pageContext = await getServerSideProps(context, authContext)
      if (!pageContext.props) {
        return pageContext
      }

      return {
        props: pack({
          error: errorMessage ? { message: errorMessage } : null,
          ...pageContext.props,
          ...authContext,
          query: {
            ...query,
            callbackUrl: callbackUrl ?? defaultCallbackUrl,
          },
        }),
      }
    } catch ({ statusCode = 500, message }) {
      return {
        props: {
          error: {
            statusCode,
            message,
          },
        },
      }
    }
  }

  // biome-ignore lint/correctness/noNestedComponentDefinitions: not relevant
  function AuthPage({ action, params, routes, error, callbackUrl, ...props }) {
    if (error && action !== Actions.SignIn) {
      return <ErrorPage {...error} />
    }

    const Page = ActionPages[action].Page

    return (
      <Page
        callbackUrl={callbackUrl}
        error={error}
        params={params}
        routes={routes}
        {...props}
      />
    )
  }
}

/**
 * @param {{
 *   children?: ReactNode
 * }} props
 */
function AuthModal({ children }) {
  const { NEXT_PUBLIC_SYNQLY_PRODUCT: product = 'mesh' } = useEnv()

  return (
    <Box className={styles.container}>
      <Image
        alt={`Synqly ${product} background`}
        aria-hidden
        className={styles.background}
        height={1632}
        priority
        src={`/images/${product}.png`}
        style={{
          filter: product === 'backoffice' ? 'invert()' : undefined,
        }}
        width={2912}
      />
      <Dialog.Root open>
        <Dialog.Content
          aria-describedby={undefined}
          className={styles.modalContent}
          size="4"
        >
          <Logo className={styles.logo} variant={product} />
          {children}
        </Dialog.Content>
      </Dialog.Root>
    </Box>
  )
}

/** @type {GetServerSideAuthProps} */
function defaultPageProps(context) {
  const { query } = context

  return {
    props: {
      query,
    },
  }
}
